import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Upload,
  Download,
  Copy,
  FileJson,
  Pencil,
  Star,
  Power,
  Trash2,
} from 'lucide-react';

import { SurveyBuilder, type SurveyTree } from '@onxite/ox-survey-builder';
import { api } from '@/lib/api/client';
import { surveysApi } from './api';
import type { XySurvey } from '@/lib/api/types';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { toast } from '@/components/ui/Toast';

/**
 * Caso 08 — Survey builder host.
 *
 * Routes:
 *   /surveys/builder        → workspace: create / import / pick a survey.
 *   /surveys/builder/:id    → editor bound to one survey, plus a toolbar
 *                             for export / clone / save-as-template.
 *
 * The host owns persistence + workspace concerns; the package owns the
 * editor surface.
 */
export function SurveyBuilderPage() {
  const t = useT();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const surveyId = id ? Number(id) : 0;
  const isPersisted = Number.isFinite(surveyId) && surveyId > 0;
  useFavoritable(t('surveyBuilder.title'));

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" /> {t('common.back')}
      </button>

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-[var(--color-fg-primary)]">
          {t('surveyBuilder.title')}
          <HelpHint section="survey-builder" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {isPersisted
            ? t('surveyBuilder.subtitle.persisted')
            : t('surveyBuilder.subtitle')}
        </p>
      </header>

      <div data-tour="builder-canvas">
        {isPersisted ? (
          <BuilderEditor surveyId={surveyId} />
        ) : (
          <BuilderWorkspace
            onOpen={(sid) => navigate(`/surveys/builder/${sid}`)}
          />
        )}
      </div>
    </div>
  );
}

// ── Editor (one survey) ─────────────────────────────────────────────────
function BuilderEditor({ surveyId }: { surveyId: number }) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const treeQuery = useQuery({
    queryKey: ['surveyBuilder', 'tree', surveyId],
    queryFn: () => api.get<SurveyTree>(`/xy-xurvey/${surveyId}/tree`),
  });
  // Survey meta carries `isTemplate` — the tree payload doesn't.
  const metaQuery = useQuery({
    queryKey: ['surveyBuilder', 'meta', surveyId],
    queryFn: () => surveysApi.get(surveyId),
  });

  const saveMutation = useMutation({
    mutationFn: (next: SurveyTree) =>
      api.put<{ ok: true; surveyId: number }>(
        `/xy-xurvey/${surveyId}/tree`,
        next,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['surveyBuilder', 'tree', surveyId],
      });
      toast({ title: t('surveyBuilder.toast.saved'), tone: 'success' });
    },
    onError: (err: any) =>
      toast({
        title: t('surveyBuilder.toast.saveFailed'),
        description: err?.message,
        tone: 'error',
      }),
  });

  const cloneMutation = useMutation({
    mutationFn: () => surveysApi.clone(surveyId),
    onSuccess: (created) => {
      toast({ title: t('surveyBuilder.toast.cloned'), tone: 'success' });
      navigate(`/surveys/builder/${created.id}`);
    },
    onError: (err: any) =>
      toast({
        title: t('surveyBuilder.toast.cloneFailed'),
        description: err?.message,
        tone: 'error',
      }),
  });

  const templateMutation = useMutation({
    mutationFn: (isTemplate: boolean) =>
      surveysApi.update(surveyId, { isTemplate }),
    onSuccess: (_data, isTemplate) => {
      queryClient.invalidateQueries({
        queryKey: ['surveyBuilder', 'meta', surveyId],
      });
      toast({
        title: isTemplate
          ? t('surveyBuilder.toast.markedTemplate')
          : t('surveyBuilder.toast.unmarkedTemplate'),
        tone: 'success',
      });
    },
    onError: (err: any) =>
      toast({
        title: t('surveyBuilder.toast.templateFailed'),
        description: err?.message,
        tone: 'error',
      }),
  });

  function exportJson() {
    if (!treeQuery.data) return;
    // Export the last-saved tree. Autosave (2s) keeps this fresh; the
    // builder also writes on every explicit Save.
    downloadJson(
      `${slug(treeQuery.data.name || 'survey')}.json`,
      treeQuery.data,
    );
    toast({ title: t('surveyBuilder.toast.exported'), tone: 'success' });
  }

  if (treeQuery.isLoading) return <Skeleton className="h-96" />;
  if (!treeQuery.data) return null;

  const isTemplate = !!metaQuery.data?.isTemplate;

  return (
    <div className="flex flex-col gap-4">
      {/* Host toolbar — workspace actions that sit outside the package. */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-2">
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<Download className="h-4 w-4" />}
          onClick={exportJson}
        >
          {t('surveyBuilder.action.export')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<Copy className="h-4 w-4" />}
          onClick={() => cloneMutation.mutate()}
          loading={cloneMutation.isPending}
        >
          {t('surveyBuilder.action.clone')}
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {isTemplate && (
            <Badge tone="accent">
              <Star className="mr-1 h-3 w-3" />
              {t('surveyBuilder.templateBadge')}
            </Badge>
          )}
          <Button
            size="sm"
            variant={isTemplate ? 'secondary' : 'primary'}
            leftIcon={<Star className="h-4 w-4" />}
            onClick={() => templateMutation.mutate(!isTemplate)}
            loading={templateMutation.isPending}
            disabled={metaQuery.isLoading}
          >
            {isTemplate
              ? t('surveyBuilder.action.unmarkTemplate')
              : t('surveyBuilder.action.markTemplate')}
          </Button>
        </div>
      </div>

      <SurveyBuilder
        tree={treeQuery.data}
        appContext={{ app: 'xotski' }}
        onSave={(next) => saveMutation.mutateAsync(next).then(() => undefined)}
        onValidate={(next) =>
          api.post(`/xy-xurvey/${surveyId}/validate`, next)
        }
        autoSaveMs={2000}
      />
    </div>
  );
}

// ── Workspace (no survey selected) ──────────────────────────────────────
function BuilderWorkspace({ onOpen }: { onOpen: (id: number) => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Branded confirm dialog state. Replaces window.confirm — `null` means
  // the dialog is closed; the selected survey awaits confirmation.
  const [pendingDelete, setPendingDelete] = useState<XySurvey | null>(null);

  const listQuery = useQuery({
    queryKey: ['surveyBuilder', 'list'],
    queryFn: () => surveysApi.list(),
  });

  const create = useMutation({
    mutationFn: (input: { name: string; tree?: SurveyTree }) =>
      createSurveyThenTree(input.name, input.tree),
    onSuccess: (newId, vars) => {
      toast({
        title: vars.tree
          ? t('surveyBuilder.toast.imported')
          : t('surveyBuilder.toast.created'),
        tone: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['surveyBuilder', 'list'] });
      onOpen(newId);
    },
    onError: (err: any) =>
      toast({
        title: t('surveyBuilder.toast.createFailed'),
        description: err?.message,
        tone: 'error',
      }),
  });

  const clone = useMutation({
    mutationFn: (id: number) => surveysApi.clone(id),
    onSuccess: (created) => {
      toast({ title: t('surveyBuilder.toast.cloned'), tone: 'success' });
      onOpen(created.id);
    },
    onError: (err: any) =>
      toast({
        title: t('surveyBuilder.toast.cloneFailed'),
        description: err?.message,
        tone: 'error',
      }),
  });

  // Disable / re-enable toggle. Status moves between 'Enable' and
  // 'Disable'; the list still shows disabled rows so the user can flip
  // them back without hunting in the DB.
  const setStatus = useMutation({
    mutationFn: (args: { id: number; status: 'Enable' | 'Disable' }) =>
      surveysApi.update(args.id, { status: args.status }),
    onSuccess: (_data, args) => {
      toast({
        title:
          args.status === 'Disable'
            ? t('surveyBuilder.toast.disabled')
            : t('surveyBuilder.toast.enabled'),
        tone: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['surveyBuilder', 'list'] });
    },
    onError: (err: any) =>
      toast({
        title: t('surveyBuilder.toast.statusFailed'),
        description: err?.message,
        tone: 'error',
      }),
  });

  // Delete is the app's soft-delete (status='Deleted'). The list query
  // already excludes Deleted rows on the BE, so the survey disappears
  // immediately. Reversible by flipping the column.
  const remove = useMutation({
    mutationFn: (id: number) => surveysApi.remove(id),
    onSuccess: () => {
      toast({ title: t('surveyBuilder.toast.deleted'), tone: 'success' });
      queryClient.invalidateQueries({ queryKey: ['surveyBuilder', 'list'] });
      setPendingDelete(null);
    },
    onError: (err: any) =>
      toast({
        title: t('surveyBuilder.toast.deleteFailed'),
        description: err?.message,
        tone: 'error',
      }),
  });

  async function onImportFile(file: File) {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const tree = normaliseImportedTree(parsed);
      if (!tree) {
        toast({
          title: t('surveyBuilder.toast.importInvalid'),
          tone: 'error',
        });
        return;
      }
      create.mutate({ name: tree.name || 'Imported survey', tree });
    } catch (err: any) {
      toast({
        title: t('surveyBuilder.toast.importInvalid'),
        description: err?.message,
        tone: 'error',
      });
    }
  }

  const surveys = listQuery.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Create + Import */}
      <Card>
        <CardHeader>
          <CardTitle>{t('surveyBuilder.create.title')}</CardTitle>
          <CardDescription>
            {t('surveyBuilder.create.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-fg-muted)]">
              {t('surveyBuilder.create.nameLabel')}
            </span>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) {
                  create.mutate({ name: name.trim() });
                }
              }}
              placeholder={t('surveyBuilder.create.namePlaceholder')}
            />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onImportFile(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="secondary"
              leftIcon={<Upload className="h-4 w-4" />}
              onClick={() => fileRef.current?.click()}
              disabled={create.isPending}
            >
              {t('surveyBuilder.action.import')}
            </Button>
            <Button
              onClick={() => create.mutate({ name: name.trim() })}
              disabled={!name.trim() || create.isPending}
              leftIcon={<Plus className="h-4 w-4" />}
              loading={create.isPending}
            >
              {t('surveyBuilder.create.submit')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing surveys */}
      <Card>
        <CardHeader>
          <CardTitle>{t('surveyBuilder.list.title')}</CardTitle>
          <CardDescription>{t('surveyBuilder.list.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : surveys.length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('surveyBuilder.list.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border-subtle)]">
              {surveys.map((s: XySurvey) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-3 py-2.5"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <FileJson className="h-4 w-4 shrink-0 text-[var(--color-fg-subtle)]" />
                    <span className="truncate text-sm font-medium text-[var(--color-fg-primary)]">
                      {s.name}
                    </span>
                    {s.isTemplate && (
                      <Badge tone="accent" className="text-[9px]">
                        {t('surveyBuilder.templateBadge')}
                      </Badge>
                    )}
                    {s.isLocked && (
                      <Badge tone="warn" className="text-[9px]">
                        {t('surveys.locked')}
                      </Badge>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<Pencil className="h-3.5 w-3.5" />}
                      onClick={() => onOpen(s.id)}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<Copy className="h-3.5 w-3.5" />}
                      onClick={() => clone.mutate(s.id)}
                      loading={clone.isPending}
                    >
                      {t('surveyBuilder.action.clone')}
                    </Button>
                    {/* Disable/Enable toggle. The list still shows
                        disabled rows (status filter excludes only
                        'Deleted'), so flipping back is one click. */}
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<Power className="h-3.5 w-3.5" />}
                      onClick={() =>
                        setStatus.mutate({
                          id: s.id,
                          status: s.status === 'Disable' ? 'Enable' : 'Disable',
                        })
                      }
                      loading={setStatus.isPending}
                    >
                      {s.status === 'Disable'
                        ? t('surveyBuilder.action.enable')
                        : t('surveyBuilder.action.disable')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={
                        <Trash2 className="h-3.5 w-3.5 text-[var(--color-error)]" />
                      }
                      onClick={() => setPendingDelete(s)}
                      disabled={
                        remove.isPending && pendingDelete?.id === s.id
                      }
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Branded delete confirmation. Replaces the native window.confirm
          alert — same colour palette as the rest of the app, keyboard
          accessible via Radix, and lives next to the mutation state. */}
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title={t('surveyBuilder.confirm.deleteTitle')}
        description={
          pendingDelete
            ? t('surveyBuilder.confirm.delete', { name: pendingDelete.name })
            : null
        }
        confirmLabel={t('common.delete')}
        tone="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

/**
 * Create a fresh `xy_survey` row, then (if a tree is supplied) push it via
 * the transactional tree endpoint. Returns the new survey id.
 */
async function createSurveyThenTree(
  name: string,
  tree?: SurveyTree,
): Promise<number> {
  const created = await surveysApi.create({
    name: name.trim(),
    // `description` / `endpointUrl` are NOT NULL with no DB default.
    description: '',
    endpointUrl: '',
    productType: 'xotski',
    defaultLanguage: tree?.defaultLanguage ?? 'en',
    languages: tree?.languages ?? ['en', 'es'],
    numberOfSteps: tree?.steps?.length ?? 0,
  });
  if (tree) {
    await api.put(`/xy-xurvey/${created.id}/tree`, {
      ...tree,
      id: created.id,
      name: name.trim(),
    });
  }
  return created.id;
}

/**
 * Validate + coerce an arbitrary parsed JSON into a `SurveyTree`. Returns
 * `null` when the shape is unusable. Tolerant of missing optional fields
 * so an export from an older builder version still imports.
 */
function normaliseImportedTree(raw: unknown): SurveyTree | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.steps)) return null;
  return {
    name: typeof o.name === 'string' ? o.name : 'Imported survey',
    productType:
      (o.productType as SurveyTree['productType']) ?? 'consumer-survey',
    defaultLanguage:
      typeof o.defaultLanguage === 'string' ? o.defaultLanguage : 'en',
    languages: Array.isArray(o.languages)
      ? (o.languages as string[])
      : ['en'],
    steps: o.steps as SurveyTree['steps'],
  };
}

function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function slug(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'survey'
  );
}
